/**
 * BITCULT Contract Service
 * Provides real-time blockchain data for rewards tracking
 */

import { Contract, BrowserProvider, JsonRpcProvider, Eip1193Provider } from 'ethers';

// Contract configuration
export const BITCULT_CONTRACT = '0x2Cb3E83c41E8BE067c32C6d9920E3f8c24945C25';
export const BSC_RPC = 'https://bsc-dataseed.binance.org/';
export const BSC_CHAIN_ID = 56;

// Minimal ABI for reward tracking functions
const BITCULT_ABI = [
    // Read functions - Token Info
    'function balanceOf(address account) view returns (uint256)',
    'function decimals() view returns (uint8)',
    'function totalSupply() view returns (uint256)',
    'function name() view returns (string)',
    'function symbol() view returns (string)',

    // Read functions - Dividend Tracking
    'function dividendTokenBalanceOf(address account) view returns (uint256)',
    'function withdrawableDividendOf(address account) view returns (uint256)',
    'function getTotalDividendsDistributed() view returns (uint256)',
    'function getNumberOfDividendTokenHolders() view returns (uint256)',
    'function getClaimWait() view returns (uint256)',
    'function getLastProcessedIndex() view returns (uint256)',
    'function getAccountDividendsInfo(address account) view returns (address, int256, int256, uint256, uint256, uint256, uint256, uint256)',
    'function DividendsToken() view returns (address)',
    'function dividendTracker() view returns (address)',

    // Read functions - Fees & Limits
    'function totalBuyFees() view returns (uint256)',
    'function totalSellFees() view returns (uint256)',
    'function _maxBuy() view returns (uint256)',
    'function _maxSell() view returns (uint256)',
    'function _maxWallet() view returns (uint256)',
    'function swapTokensAtAmount() view returns (uint256)',

    // Read functions - Contract State
    'function gasForProcessing() view returns (uint256)',
    'function burning() view returns (bool)',
    'function contractTokenBalanceAmount() view returns (uint256)',
    'function liquidityRelease20Percent() view returns (bool)',
    'function getLiquidityReleaseTimeInSeconds() view returns (uint256)',

    // Read functions - Addresses
    'function uniswapV2Pair() view returns (address)',
    'function owner() view returns (address)',

    // Events
    'event SendDividends(uint256 tokensSwapped, uint256 amount)',
    'event Transfer(address indexed from, address indexed to, uint256 value)'
];

// Interface for account dividend info
export interface AccountDividendInfo {
    account: string;
    index: bigint;
    iterationsUntilProcessed: bigint;
    withdrawableDividends: bigint;
    totalDividends: bigint;
    lastClaimTime: bigint;
    nextClaimTime: bigint;
    secondsUntilAutoClaimAvailable: bigint;
}

// Interface for holder rewards data
export interface HolderRewardsData {
    tokenBalance: bigint;
    tokenBalanceFormatted: string;
    withdrawableDividends: bigint;
    withdrawableDividendsFormatted: string;
    totalDividendsEarned: bigint;
    totalDividendsEarnedFormatted: string;
    lastClaimTime: Date | null;
    nextClaimTime: Date | null;
    sharePercentage: number;
}

// Interface for global stats
export interface GlobalRewardsStats {
    // Dividend Info
    totalDividendsDistributed: bigint;
    totalDividendsDistributedFormatted: string;
    numberOfHolders: bigint;
    claimWaitSeconds: bigint;
    lastProcessedIndex: bigint;

    // Token Info
    totalSupply: bigint;
    totalSupplyFormatted: string;

    // Fees
    totalBuyFees: bigint;
    totalSellFees: bigint;

    // Limits
    maxBuy: bigint;
    maxBuyFormatted: string;
    maxSell: bigint;
    maxSellFormatted: string;
    maxWallet: bigint;
    maxWalletFormatted: string;
    swapTokensAtAmount: bigint;
    swapTokensAtAmountFormatted: string;

    // Contract State
    gasForProcessing: bigint;
    burningEnabled: boolean;
    contractTokenBalance: bigint;
    contractTokenBalanceFormatted: string;
    liquidityRelease20Percent: boolean;
    liquidityReleaseTimeSeconds: bigint;

    // Addresses
    dividendsToken: string;
    dividendTracker: string;
    uniswapV2Pair: string;
    owner: string;
}

// EIP-1193 Provider interface for wallet
interface EthereumProvider extends Eip1193Provider {
    on(event: string, callback: (...args: unknown[]) => void): void;
    removeListener(event: string, callback: (...args: unknown[]) => void): void;
}

// Get ethereum provider from window
function getEthereumProvider(): EthereumProvider | null {
    if (typeof window === 'undefined') return null;
    const eth = (window as { ethereum?: EthereumProvider }).ethereum;
    return eth || null;
}

// Read-only provider for BSC (no wallet needed)
let readOnlyProvider: JsonRpcProvider | null = null;
let readOnlyContract: Contract | null = null;

// Connected provider (requires wallet)
let connectedProvider: BrowserProvider | null = null;

/**
 * Get read-only provider for BSC
 */
export function getReadOnlyProvider(): JsonRpcProvider {
    if (!readOnlyProvider) {
        readOnlyProvider = new JsonRpcProvider(BSC_RPC);
    }
    return readOnlyProvider;
}

/**
 * Get read-only contract instance
 */
export function getReadOnlyContract(): Contract {
    if (!readOnlyContract) {
        readOnlyContract = new Contract(BITCULT_CONTRACT, BITCULT_ABI, getReadOnlyProvider());
    }
    return readOnlyContract;
}

/**
 * Connect to wallet and get signer
 */
export async function connectWallet(): Promise<{ provider: BrowserProvider; address: string } | null> {
    const ethereum = getEthereumProvider();
    if (!ethereum) {
        console.error('No Web3 wallet detected');
        return null;
    }

    try {
        connectedProvider = new BrowserProvider(ethereum);

        // Request accounts
        await connectedProvider.send('eth_requestAccounts', []);

        // Check if on BSC
        const network = await connectedProvider.getNetwork();
        if (Number(network.chainId) !== BSC_CHAIN_ID) {
            // Try to switch to BSC
            try {
                await ethereum.request({
                    method: 'wallet_switchEthereumChain',
                    params: [{ chainId: '0x38' }] // 56 in hex
                });
            } catch (switchError: unknown) {
                // If BSC not added, add it
                const error = switchError as { code?: number };
                if (error.code === 4902) {
                    await ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [{
                            chainId: '0x38',
                            chainName: 'BNB Smart Chain',
                            nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
                            rpcUrls: [BSC_RPC],
                            blockExplorerUrls: ['https://bscscan.com']
                        }]
                    });
                } else {
                    throw switchError;
                }
            }
            // Refresh provider after network switch
            connectedProvider = new BrowserProvider(ethereum);
        }

        const signer = await connectedProvider.getSigner();
        const address = await signer.getAddress();

        return { provider: connectedProvider, address };
    } catch (error) {
        console.error('Failed to connect wallet:', error);
        return null;
    }
}

/**
 * Get holder's token balance
 */
export async function getTokenBalance(address: string): Promise<bigint> {
    const contract = getReadOnlyContract();
    return await contract.balanceOf(address);
}

/**
 * Get holder's withdrawable dividends (in wei)
 */
export async function getWithdrawableDividends(address: string): Promise<bigint> {
    const contract = getReadOnlyContract();
    return await contract.withdrawableDividendOf(address);
}

/**
 * Get detailed dividend info for an account
 */
export async function getAccountDividendInfo(address: string): Promise<AccountDividendInfo> {
    const contract = getReadOnlyContract();
    const result = await contract.getAccountDividendsInfo(address);

    return {
        account: result[0],
        index: result[1],
        iterationsUntilProcessed: result[2],
        withdrawableDividends: result[3],
        totalDividends: result[4],
        lastClaimTime: result[5],
        nextClaimTime: result[6],
        secondsUntilAutoClaimAvailable: result[7]
    };
}

/**
 * Get global rewards statistics
 */
export async function getGlobalStats(): Promise<GlobalRewardsStats> {
    const contract = getReadOnlyContract();

    const [
        totalDistributed,
        holderCount,
        claimWait,
        lastProcessedIndex,
        totalSupply,
        totalBuyFees,
        totalSellFees,
        maxBuy,
        maxSell,
        maxWallet,
        swapTokensAtAmount,
        gasForProcessing,
        burningEnabled,
        contractTokenBalance,
        liquidityRelease20Percent,
        liquidityReleaseTime,
        dividendsToken,
        dividendTracker,
        uniswapV2Pair,
        owner
    ] = await Promise.all([
        contract.getTotalDividendsDistributed(),
        contract.getNumberOfDividendTokenHolders(),
        contract.getClaimWait(),
        contract.getLastProcessedIndex(),
        contract.totalSupply(),
        contract.totalBuyFees(),
        contract.totalSellFees(),
        contract._maxBuy(),
        contract._maxSell(),
        contract._maxWallet(),
        contract.swapTokensAtAmount(),
        contract.gasForProcessing(),
        contract.burning(),
        contract.contractTokenBalanceAmount(),
        contract.liquidityRelease20Percent(),
        contract.getLiquidityReleaseTimeInSeconds(),
        contract.DividendsToken(),
        contract.dividendTracker(),
        contract.uniswapV2Pair(),
        contract.owner()
    ]);

    return {
        // Dividend Info
        totalDividendsDistributed: totalDistributed,
        totalDividendsDistributedFormatted: formatBTC(totalDistributed),
        numberOfHolders: holderCount,
        claimWaitSeconds: claimWait,
        lastProcessedIndex: lastProcessedIndex,

        // Token Info
        totalSupply: totalSupply,
        totalSupplyFormatted: formatTokens(totalSupply),

        // Fees
        totalBuyFees: totalBuyFees,
        totalSellFees: totalSellFees,

        // Limits
        maxBuy: maxBuy,
        maxBuyFormatted: formatTokens(maxBuy),
        maxSell: maxSell,
        maxSellFormatted: formatTokens(maxSell),
        maxWallet: maxWallet,
        maxWalletFormatted: formatTokens(maxWallet),
        swapTokensAtAmount: swapTokensAtAmount,
        swapTokensAtAmountFormatted: formatTokens(swapTokensAtAmount),

        // Contract State
        gasForProcessing: gasForProcessing,
        burningEnabled: burningEnabled,
        contractTokenBalance: contractTokenBalance,
        contractTokenBalanceFormatted: formatTokens(contractTokenBalance),
        liquidityRelease20Percent: liquidityRelease20Percent,
        liquidityReleaseTimeSeconds: liquidityReleaseTime,

        // Addresses
        dividendsToken: dividendsToken,
        dividendTracker: dividendTracker,
        uniswapV2Pair: uniswapV2Pair,
        owner: owner
    };
}

/**
 * Get comprehensive rewards data for a holder
 */
export async function getHolderRewardsData(address: string): Promise<HolderRewardsData> {
    const contract = getReadOnlyContract();

    const [balance, dividendInfo, totalSupply] = await Promise.all([
        contract.balanceOf(address),
        getAccountDividendInfo(address),
        contract.totalSupply()
    ]);

    // Calculate share percentage
    const sharePercentage = totalSupply > 0n
        ? Number((balance * 10000n) / totalSupply) / 100
        : 0;

    return {
        tokenBalance: balance,
        tokenBalanceFormatted: formatTokens(balance),
        withdrawableDividends: dividendInfo.withdrawableDividends,
        withdrawableDividendsFormatted: formatBTC(dividendInfo.withdrawableDividends),
        totalDividendsEarned: dividendInfo.totalDividends,
        totalDividendsEarnedFormatted: formatBTC(dividendInfo.totalDividends),
        lastClaimTime: dividendInfo.lastClaimTime > 0n
            ? new Date(Number(dividendInfo.lastClaimTime) * 1000)
            : null,
        nextClaimTime: dividendInfo.nextClaimTime > 0n
            ? new Date(Number(dividendInfo.nextClaimTime) * 1000)
            : null,
        sharePercentage
    };
}

/**
 * Format token amount (9 decimals for BITCULT)
 */
export function formatTokens(amount: bigint, decimals: number = 9): string {
    const divisor = 10n ** BigInt(decimals);
    const whole = amount / divisor;

    if (whole >= 1000000n) {
        return `${(Number(whole) / 1000000).toFixed(2)}M`;
    } else if (whole >= 1000n) {
        return `${(Number(whole) / 1000).toFixed(2)}K`;
    }

    return whole.toLocaleString();
}

/**
 * Format BTC amount (18 decimals, assuming BTCB on BSC)
 */
export function formatBTC(amount: bigint, decimals: number = 18): string {
    const divisor = 10n ** BigInt(decimals);
    const value = Number(amount) / Number(divisor);
    return value.toFixed(8);
}

/**
 * Format USD value
 */
export function formatUSD(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
}

/**
 * Check if wallet is connected and on BSC
 */
export async function isWalletConnected(): Promise<{ connected: boolean; address: string | null; onBSC: boolean }> {
    const ethereum = getEthereumProvider();
    if (!ethereum) {
        return { connected: false, address: null, onBSC: false };
    }

    try {
        const provider = new BrowserProvider(ethereum);
        const accounts = await provider.listAccounts();

        if (accounts.length === 0) {
            return { connected: false, address: null, onBSC: false };
        }

        const network = await provider.getNetwork();
        const onBSC = Number(network.chainId) === BSC_CHAIN_ID;

        return {
            connected: true,
            address: accounts[0].address,
            onBSC
        };
    } catch {
        return { connected: false, address: null, onBSC: false };
    }
}

/**
 * Listen for account/network changes
 */
export function subscribeToWalletChanges(callback: (address: string | null) => void): () => void {
    const ethereum = getEthereumProvider();
    if (!ethereum) {
        return () => {};
    }

    const handleAccountsChanged = (accounts: unknown) => {
        const accountList = accounts as string[];
        callback(accountList.length > 0 ? accountList[0] : null);
    };

    const handleChainChanged = () => {
        // Reload on chain change
        window.location.reload();
    };

    ethereum.on('accountsChanged', handleAccountsChanged);
    ethereum.on('chainChanged', handleChainChanged);

    return () => {
        ethereum.removeListener('accountsChanged', handleAccountsChanged);
        ethereum.removeListener('chainChanged', handleChainChanged);
    };
}
